<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'

import ChatInputError from '@shared/components/ChatInputError.vue'
import ChatProgress from '@shared/components/ChatProgress.vue'
import SendMessage from '@shared/components/SendMessage.vue'
import AppTooltip from '@shared/components/ui/AppTooltip.vue'
import EventBus, { GlobalEvents } from '@shared/event-bus'
import { MessageCreateData } from '@shared/models/interfaces/messages'
import { Nullable } from '@shared/models/types/primary'
import { useGeneralStore } from '@shared/store/general'
import { useChatInputActions } from '@shared/utils/composables/chat-messages'
import {
  handleInputKey,
  removeEmojisFromString,
} from '@shared/utils/helpers/input-events'
import { handleTyping } from '@shared/utils/helpers/input-typing'
import { generateUint8ArrayFromFile } from '@shared/utils/helpers/uint8array-formatters'

import { MessageType } from '@protos/chat/message_type_pb'

import { useVideoCallStore } from '@pc/store/video-call'

import { useAttachmentSelect } from './composables'

const EmojiPicker = defineAsyncComponent(
  () => import('@shared/components/EmojiPicker/EmojiPicker.vue')
)
const AppIcon = defineAsyncComponent(
  () => import('@shared/components/ui/AppIcon.vue')
)

const videoCallStore = useVideoCallStore()

const { isCallIncomingShown } = storeToRefs(videoCallStore)

const props = withDefaults(
  defineProps<{
    maxSize: Nullable<number>
    allowedTypes?: string[]
    error: Nullable<string>
    loading?: boolean
    scrollCallback?: () => void
  }>(),
  {
    allowedTypes: () => [],
    error: null,
    loading: false,
  }
)
const emit = defineEmits<{
  (event: 'send-message', payload: MessageCreateData): void
  (event: 'typing', isTyping: boolean): void
}>()

const { t } = useI18n()

const attachmentsToSend = ref(t('attachmentsToSend'))
const poweredByQliqSoft = ref(t('pt.labels.poweredByQliqSoft'))
const writeReply = ref(t('labels.writeReply'))

const {
  selectFile,
  fileInputRef,
  inputRef,
  focusInput,
  files,
  removeFile,
  attachmentError,
  setIsInputFocused,
  resetAttachments,
} = useChatInputActions()

const { showEmojis, showEmojisPicker, isTextOnly, isAbleToSendAttachments } =
  useAttachmentSelect()
const generalStore = useGeneralStore()
const messageText = ref('')

const resetInputState = () => {
  messageText.value = ''
  files.value = []
}

const isDisabledFileInput = computed(() => {
  return !!files.value.length
})
const errorMessage = computed(() => props.error)
const isFilesEmpty = computed(() => !files.value?.length)
const isMessageTextEmpty = computed(() => !messageText.value.length)

const sendMessage = async () => {
  focusInput(true)

  if (props.loading) {
    return
  }

  if (!messageText.value.trim() && !files.value?.length) {
    return
  }

  if (attachmentError.value) {
    return
  }

  const dtoFiles = []
  for (const file of files.value) {
    const content = await generateUint8ArrayFromFile(file)
    dtoFiles.push({
      content,
      originalFilename: file.name,
      contentType: file.type,
    })
  }

  const messageType = files.value?.length ? MessageType.IMAGE : MessageType.TEXT

  const messageParams = {
    message: {
      messageType,
      text: messageText.value,
      attachments: [],
    },
    files: dtoFiles,
  }

  emit('send-message', messageParams)
}

const typingHandler = handleTyping((isTyping) => {
  isTyping && emit('typing', isTyping)
})

const handleKey = handleInputKey(sendMessage)

const handleInput = (value: string) => {
  typingHandler()

  // if the emojis are restricted, replace them with empty string

  if (!showEmojis.value) {
    messageText.value = removeEmojisFromString(value)
  }
}

const applyEmojis = (emoji: string) =>
  (messageText.value = messageText.value
    ? `${messageText.value} ${emoji}`
    : emoji)

const setIsFileUploading = () => {
  // blur the input field to hide keyboard first when the file input is clicked
  inputRef.value?.inputRef.blur()

  setTimeout(() => {
    fileInputRef.value?.click()
  }, 200)

  generalStore.isFileUploading = true
}

const removeIsFileUploading = () => {
  generalStore.isFileUploading = false
}

const onSelectFile = (event: Event) => {
  selectFile(event, {
    maxSize: props?.maxSize,
    allowedTypes: props?.allowedTypes,
  })
  nextTick(() => {
    removeIsFileUploading()
  })
}

watch(isCallIncomingShown, (value) => {
  if (value) {
    inputRef.value?.inputRef.blur()
  }
})

onMounted(() => {
  nextTick(() => focusInput(true))
  EventBus.on(GlobalEvents.FocusInput, focusInput)
  EventBus.on(GlobalEvents.ResetInputField, resetInputState)
})

onUnmounted(() => {
  EventBus.off(GlobalEvents.ResetInputField, resetInputState)
  EventBus.off(GlobalEvents.FocusInput, focusInput)
})
</script>

<template>
  <div class="tw-flex tw-flex-col">
    <ChatInputError :error="errorMessage" />

    <div
      class="chat-patient-input input-focus tw-bg-gray-4 tw-py-2 tw-px-4 tw-flex tw-flex-col"
    >
      <ChatProgress class="progress tw-absolute tw-w-full tw-left-0" />
      <div class="tw-flex tw-items-center">
        <div class="tw-flex tw-items-center tw-mb-0 tw-flex-1">
          <label
            v-if="isAbleToSendAttachments"
            class="attachment tw-cursor-pointer tw-h-full tw-flex tw-items-center tw-justify-center"
            for="file-input"
            data-file-input
          >
            <AppTooltip
              :content="t('chat.labels.oneFilePerMessage')"
              placement="top"
              :disabled="!isDisabledFileInput"
            >
              <AppButton
                text
                only-icon
                circle
                data-file-upload
                @click="setIsFileUploading"
              >
                <AppIcon
                  :class="{ 'tw-opacity-40': isDisabledFileInput }"
                  shape="paperclip"
                  size="20"
                />
              </AppButton>
            </AppTooltip>
            <input
              id="file-input"
              :key="attachmentError"
              ref="fileInputRef"
              class="tw-hidden"
              type="file"
              :disabled="isDisabledFileInput"
              @change="onSelectFile"
            />
          </label>

          <div
            :class="['input-wrapper tw-flex-1', { 'is-error': errorMessage }]"
          >
            <AppInput
              ref="inputRef"
              v-model="messageText"
              class="tw-text-base tw-shadow-none"
              type="textarea"
              :autosize="{
                minRows: 1,
                maxRows: 3,
              }"
              resize="none"
              :placeholder="writeReply"
              @input="handleInput"
              @keydown.enter="handleKey"
              @focus="setIsInputFocused(true)"
              @blur="setIsInputFocused(false, scrollCallback)"
            >
              <template #append>
                <AppTooltip
                  v-if="isMessageTextEmpty"
                  :content="poweredByQliqSoft"
                  placement="top"
                  effect="dark"
                >
                  <div
                    class="tw-hidden sm:tw-flex tw-items-center tw-text-gray-500 tw-font-thin tw-mr-3 tw-whitespace-nowrap"
                  >
                    <img
                      class="tw-mr-2"
                      width="15"
                      height="15"
                      src="@pc/assets/images/chat-powered.svg?inline"
                      alt="Chat powered icon"
                    />
                  </div>
                </AppTooltip>
              </template>
            </AppInput>
            <span
              v-if="attachmentError"
              class="tw-text-sm tw-text-red-500 tw-pl-2"
            >
              {{ attachmentError }}
              <AppIcon
                class="tw-cursor-pointer"
                shape="times"
                size="28"
                color="warning"
                @click="resetAttachments"
              />
            </span>
          </div>
        </div>

        <div class="tw-flex tw-items-center tw-justify-end">
          <EmojiPicker
            v-if="showEmojisPicker && !isTextOnly"
            @emoji-select="applyEmojis"
          >
            <AppButton
              id="chat-user-input-apply-emoji_btn_id"
              text
              circle
              class="tw-mx-2"
            >
              <AppIcon shape="happy-face" size="20" />
            </AppButton>
          </EmojiPicker>

          <SendMessage
            :loading="loading"
            :error="errorMessage"
            @action="sendMessage"
          />
        </div>
      </div>

      <div
        v-if="!isFilesEmpty"
        class="tw-flex tw-items-center tw-mt-2 tw-flex-wrap tw-px-2"
      >
        <span class="tw-text-xs">
          {{ attachmentsToSend }}
        </span>

        <div
          v-for="(file, idx) in files"
          :key="file.name"
          class="tw-border-black tw-rounded-2xl tw-p-2 tw-flex tw-items-center tw-ml-2 tw-border"
        >
          <span class="tw-text-xs tw-inline-block tw-mx-2">
            {{ file.name }}
          </span>

          <AppIcon
            shape="times"
            size="24"
            color="error"
            class="tw-cursor-pointer"
            @click="removeFile(idx)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.progress {
  top: -6px;
}

.attachment {
  width: 40px;
}

:deep(.el-button--primary) {
  background-color: var(--branding-color, var(--el-color-primary));
  border-color: var(--branding-color, var(--el-color-primary));
}

:deep(.el-input-group__append) {
  @apply tw-shadow-none tw-bg-white tw-hidden sm:tw-flex;
}

:deep(.el-input__wrapper) {
  @apply tw-shadow-none;
}

.input-wrapper {
  background-image: none;
  border-radius: var(--el-input-border-radius, var(--el-border-radius-base));
  transition: var(--el-transition-box-shadow);
  box-shadow: 0 0 0 1px var(--el-input-border-color, var(--el-border-color))
    inset;
}

:deep(.is-error .el-input__wrapper) {
  @apply tw-border-red-500 tw-border tw-border-solid;

  &.is-focus {
    border-color: $error;
    box-shadow: 0 0 1px 1px #ff5252;
  }
}
</style>
